/**
 * Cliente API — sustituye todas las llamadas a localStorage.
 * El token JWT se guarda en localStorage solo para persistir la sesión;
 * nunca se usa para lógica de negocio.
 */

const BASE = (import.meta.env.VITE_API_URL ?? "") + "/api";

let _token = null;

export function getStoredToken() {
  if (_token) return _token;
  try {
    _token = localStorage.getItem("atenea_token");
  } catch {
    _token = null;
  }
  return _token;
}

export function storeToken(t) {
  _token = t;
  try {
    if (t) localStorage.setItem("atenea_token", t);
    else localStorage.removeItem("atenea_token");
  } catch {}
}

function authHeaders() {
  const t = getStoredToken();
  return {
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function req(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { ...authHeaders(), ...(opts.headers ?? {}) },
  });

  if (res.status === 401) {
    storeToken(null);
    throw new ApiError("UNAUTHORIZED", 401);
  }

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = text;
  }

  if (!res.ok) {
    throw new ApiError(
      (typeof data === "object" && data?.error) ? data.error : `HTTP ${res.status}`,
      res.status,
    );
  }

  return data;
}

// ─── Auth ──────────────────────────────────────────────────────────────────

export async function apiCheckSetup() {
  const data = await req(`${BASE}/auth/status`);
  return data; // { hasUsers: boolean }
}

export async function apiSetup(email, password, name) {
  const data = await req(`${BASE}/auth/setup`, {
    method: "POST",
    body: JSON.stringify({ email, password, name }),
  });
  storeToken(data.token);
  return data; // { token, user }
}

export async function apiLogin(email, password) {
  const data = await req(`${BASE}/auth/login`, {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  storeToken(data.token);
  return data; // { token, user }
}

export function apiLogout() {
  storeToken(null);
  try {
    localStorage.removeItem("atenea_user");
  } catch {}
}

export async function apiGetUsers() {
  return req(`${BASE}/auth/users`);
}

export async function apiCreateUser(email, password, name, role = "editor") {
  return req(`${BASE}/auth/users`, {
    method: "POST",
    body: JSON.stringify({ email, password, name, role }),
  });
}

export async function apiDeleteUser(id) {
  return req(`${BASE}/auth/users/${id}`, { method: "DELETE" });
}

export async function apiChangePassword(currentPassword, newPassword) {
  return req(`${BASE}/auth/change-password`, {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// ─── Proyectos ─────────────────────────────────────────────────────────────

export async function apiGetProyectos() {
  return req(`${BASE}/proyectos`);
}

/** Reemplaza todos los proyectos (operación de guardado principal). */
export async function apiSaveProyectos(list) {
  return req(`${BASE}/proyectos/bulk`, {
    method: "PUT",
    body: JSON.stringify(list),
  });
}

// ─── Backup / Restore ──────────────────────────────────────────────────────

const CONFIG_KEYS = ["equipos", "rosterweek", "festivos", "objfact", "avance"];

/** Exporta todos los datos a un objeto JSON listo para descargar. */
export async function apiExportAll() {
  const [proyectos, ...configs] = await Promise.all([
    apiGetProyectos(),
    ...CONFIG_KEYS.map((k) => apiGetConfig(k)),
  ]);
  const config = {};
  CONFIG_KEYS.forEach((k, i) => { config[k] = configs[i]; });
  return { version: 1, exportedAt: new Date().toISOString(), proyectos, config };
}

/** Restaura todos los datos desde un objeto exportado previamente. */
export async function apiImportAll(backup) {
  if (!backup || backup.version !== 1) throw new Error("Formato de backup no reconocido");
  await apiSaveProyectos(backup.proyectos ?? []);
  await Promise.all(
    CONFIG_KEYS.map((k) => backup.config?.[k] != null ? apiSetConfig(k, backup.config[k]) : Promise.resolve())
  );
}

// ─── Config ────────────────────────────────────────────────────────────────

export async function apiGetConfig(key) {
  const data = await req(`${BASE}/config/${key}`);
  return data.value; // null si no existe
}

export async function apiSetConfig(key, value) {
  return req(`${BASE}/config/${key}`, {
    method: "PUT",
    body: JSON.stringify({ value }),
  });
}
